/**
 * GFG-272 — Reports: Top Gifts. Pure helpers backing the panel's controls and formatting — no
 * fetching, no React, so every branch here is covered by `reportsTopGifts-helper.test.ts` without
 * a DOM or a network mock.
 */
import type { FilterOption } from "@/components/BlotterFilterBar";
import { IMPACT_REPORT_DATE_PRESET_LABELS } from "@/constants/impactReport";
import { PCT_DECIMALS, TOP_GIFTS_TEXT } from "@/constants/reportsTopGifts";
import type { ImpactDateSelection } from "@/interfaces/impactReport";
import type { TopGiftsListParams, TopGiftsSelection } from "@/interfaces/reportsTopGifts";
import { formatDateLong } from "@/utils/date-format";
import { resolveImpactDateWindow } from "@/utils/impact-report-filter-helper";

/**
 * Selection -> API list params. `campaign_id` is set only when there is a selection. Dates come
 * from `resolveImpactDateWindow(selection.date, now)` (`@/utils/impact-report-filter-helper`),
 * which returns `null` for all time and for an incomplete custom range — in both cases NEITHER
 * date field is sent. `includeFilterOptions` is always `true`.
 */
export function toTopGiftsParams(selection: TopGiftsSelection, now: Date): TopGiftsListParams {
  const window = resolveImpactDateWindow(selection.date, now);

  return {
    ...(selection.campaignIds.length > 0 ? { campaign_id: [...selection.campaignIds] } : {}),
    ...(window ? { redeemedFrom: window.dateFrom, redeemedTo: window.dateTo } : {}),
    includeFilterOptions: true,
  };
}

/** Adds `id` at the end when absent, removes it when present; the order of the rest is preserved. */
export function toggleCampaignSelection(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((it) => it !== id) : [...selected, id];
}

/**
 * `[]` -> `TOP_GIFTS_TEXT.allCampaigns`; exactly one selected id that exists in `options` -> that
 * option's `label`; any other case -> `TOP_GIFTS_TEXT.campaignsSelected(n)`.
 */
export function campaignButtonLabel(
  selected: readonly string[],
  options: readonly FilterOption[],
): string {
  if (selected.length === 0) return TOP_GIFTS_TEXT.allCampaigns;

  if (selected.length === 1) {
    const match = options.find((option) => option.value === selected[0]);
    if (match) return match.label;
  }

  return TOP_GIFTS_TEXT.campaignsSelected(selected.length);
}

/**
 * A preset -> `IMPACT_REPORT_DATE_PRESET_LABELS[preset]`; a COMPLETE custom range ->
 * `` `${formatDateLong(from)} – ${formatDateLong(to)}` `` (the same format as the Impact Report's
 * pill, `components/impactReport/ImpactFilterPills.tsx:179`; the separator is an en-dash `–`
 * U+2013); an INCOMPLETE custom range -> the "All time" label.
 */
export function periodButtonLabel(date: ImpactDateSelection, now: Date): string {
  if (date.preset !== "custom") return IMPACT_REPORT_DATE_PRESET_LABELS[date.preset];

  const window = resolveImpactDateWindow(date, now);
  if (!window) return IMPACT_REPORT_DATE_PRESET_LABELS.allTime;

  return `${formatDateLong(window.dateFrom)} – ${formatDateLong(window.dateTo)}`;
}

/** Shared clamp: `NaN` treated as 0, then clamped to `[0, 1]` — the one rounding rule both callers use. */
function clampPct(pct: number): number {
  const safe = Number.isNaN(pct) ? 0 : pct;
  return Math.min(1, Math.max(0, safe));
}

/** `NaN` treated as 0; clamped to `[0, 1]`; formatted as `(pct * 100).toFixed(PCT_DECIMALS) + "%"`. */
export function formatPct(pct: number): string {
  return `${(clampPct(pct) * 100).toFixed(PCT_DECIMALS)}%`;
}

/** Same clamp/NaN handling as `formatPct`; returns `` `${Number((clampPct(pct) * 100).toFixed(PCT_DECIMALS))}%` `` for a CSS width. */
export function pctBarWidth(pct: number): string {
  return `${Number((clampPct(pct) * 100).toFixed(PCT_DECIMALS))}%`;
}

/** Delegates to `TOP_GIFTS_TEXT.countLabel`. */
export function topGiftsCountLabel(totalGifts: number, totalRedemptions: number): string {
  return TOP_GIFTS_TEXT.countLabel(totalGifts, totalRedemptions);
}
